import { createEffect, createSignal } from "solid-js";
import { useLocation, useNavigate, useSearch } from "@tanstack/solid-router";

/**
 * Layout-specific UI state: mobile sidebar and shortcuts modal.
 *
 * The shortcuts modal is deep-linkable via `?modal=shortcuts` (global URL
 * state, read with `strict:false` because `_app` is a layout route without its own
 * typed search). Thus `…?modal=shortcuts` reproduces the exact screen and browser
 * back dismisses it. The mobile sidebar is transient chrome and NOT
 * navigable state: it closes on every route change by design.
 */
export function useAppDialogs() {
  const location = useLocation();
  const navigate = useNavigate();
  // `strict:false`: `_app` is a layout route without its own typed search and
  // `modal` does not belong to any `validateSearch`, so we read the URL in a
  // generic fashion and narrow the value manually (without type-assertion).
  const urlSearch = useSearch({ strict: false });

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);

  createEffect(() => {
    void location().pathname;
    setIsSidebarOpen(false);
  });

  const modalParam = (): unknown => (urlSearch() as Record<string, unknown>).modal;
  const shortcutsOpen = () => modalParam() === "shortcuts";
  const setShortcutsOpen = (open: boolean) => {
    // Destination is the current route (dynamic pathname), so the search type
    // is not known statically: we preserve previous params and only toggle
    // `modal`. The reducer widens to a generic search type because TanStack
    // cannot infer the shape for a dynamic `to`.
    const reducer: (prev: Record<string, unknown>) => Record<string, unknown> = (prev) => ({
      ...prev,
      modal: open ? "shortcuts" : undefined,
    });
    void navigate({
      to: location().pathname,
      search: reducer,
      replace: true,
    });
  };

  return {
    isSidebarOpen,
    setIsSidebarOpen,
    shortcutsOpen,
    setShortcutsOpen,
  };
}
